import React, { useState } from 'react';
import './GradeDetailView.css';

function GradeDetailView({ grade, assignment, student, onClose }) {
  const [showComments, setShowComments] = useState(false);

  if (!grade || !assignment) {
    return (
      <div className="grade-detail-overlay" onClick={onClose}>
        <div className="grade-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Grade Details</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-content">
            <p>No grade information available</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate percentage and letter grade (matching iOS GradingScale)
  const calculateGradeInfo = (grade) => {
    if (!grade || grade.score === undefined || grade.maxScore === undefined || grade.maxScore === 0) {
      return {
        percentage: 0,
        letterGrade: 'N/A',
        status: 'N/A'
      };
    }

    const percentage = (grade.score / grade.maxScore) * 100;
    
    // Use the same grading scale as iOS (GradingScale.defaultScale)
    let letterGrade = 'F';
    if (percentage >= 97) letterGrade = 'A+';
    else if (percentage >= 93) letterGrade = 'A';
    else if (percentage >= 90) letterGrade = 'A-';
    else if (percentage >= 87) letterGrade = 'B+';
    else if (percentage >= 83) letterGrade = 'B';
    else if (percentage >= 80) letterGrade = 'B-';
    else if (percentage >= 77) letterGrade = 'C+';
    else if (percentage >= 73) letterGrade = 'C';
    else if (percentage >= 70) letterGrade = 'C-';
    else if (percentage >= 67) letterGrade = 'D+';
    else if (percentage >= 63) letterGrade = 'D';
    else if (percentage >= 60) letterGrade = 'D-';

    return {
      percentage: Math.round(percentage * 10) / 10, // Round to 1 decimal place
      letterGrade,
      status: grade.status || 'GRADED'
    };
  };

  const gradeInfo = calculateGradeInfo(grade);

  const getLetterGradeColor = (letterGrade) => {
    if (letterGrade === 'A' || letterGrade === 'A+') return '#28a745';
    if (letterGrade === 'B' || letterGrade === 'B+') return '#17a2b8';
    if (letterGrade === 'C' || letterGrade === 'C+') return '#ffc107';
    if (letterGrade === 'D' || letterGrade === 'D+') return '#fd7e14';
    if (letterGrade === 'F') return '#dc3545';
    return '#6c757d';
  };

  return (
    <div className="grade-detail-overlay" onClick={onClose}>
      <div className="grade-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Grade Details</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-content">
          {/* Assignment Information */}
          <div className="section">
            <h4>Assignment Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Title:</label>
                <span>{assignment.title || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Type:</label>
                <span>{assignment.assignmentType || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Due Date:</label>
                <span>
                  {assignment.dueDate?.toDate?.() ? 
                    new Date(assignment.dueDate.toDate()).toLocaleDateString() : 
                    assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 
                    'No due date'
                  }
                </span>
              </div>
              <div className="info-item">
                <label>Max Score:</label>
                <span>{assignment.maxScore || 'N/A'}</span>
              </div>
              {assignment.description && (
                <div className="info-item full-width">
                  <label>Description:</label>
                  <p>{assignment.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Grade Information */}
          <div className="section">
            <h4>Grade Information</h4>
            <div className="grade-summary">
              <div className="score-display">
                <div className="score-circle" style={{ borderColor: getLetterGradeColor(gradeInfo.letterGrade) }}>
                  <span className="score-number">{grade.score}</span>
                  <span className="score-total">/ {grade.maxScore}</span>
                </div>
                <div className="score-percentage" style={{ color: getLetterGradeColor(gradeInfo.letterGrade) }}>
                  {gradeInfo.percentage?.toFixed(1) || 'N/A'}%
                </div>
              </div>
              
              <div className="letter-grade" style={{ backgroundColor: getLetterGradeColor(gradeInfo.letterGrade) }}>
                {gradeInfo.letterGrade || 'N/A'}
              </div>
            </div>

            <div className="grade-details">
              <div className="info-item">
                <label>Status:</label>
                <span className={`status-badge ${grade.status || 'pending'}`}>
                  {grade.status ? grade.status.charAt(0).toUpperCase() + grade.status.slice(1) : 'Pending'}
                </span>
              </div>
              <div className="info-item">
                <label>Graded Date:</label>
                <span>
                  {grade.createdAt?.toDate?.() ? 
                    new Date(grade.createdAt.toDate()).toLocaleDateString() : 
                    'N/A'
                  }
                </span>
              </div>
              <div className="info-item">
                <label>Teacher:</label>
                <span>{grade.teacherName || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Student Information */}
          <div className="section">
            <h4>Student Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Name:</label>
                <span>{student?.fullName || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Student ID:</label>
                <span>{student?.studentId || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Class:</label>
                <span>{assignment.teacherName ? assignment.teacherName + "'s Class" : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="section">
            <div className="section-header">
              <h4>Comments</h4>
              <button 
                className="toggle-comments"
                onClick={() => setShowComments(!showComments)}
              >
                {showComments ? 'Hide' : 'Show'} Comments
              </button>
            </div>
            
            {showComments && (
              <div className="comments-section">
                {grade.comments ? (
                  <div className="comment-box">
                    <p>{grade.comments}</p>
                    <small className="comment-date">
                      {grade.createdAt?.toDate?.() ? 
                        `Posted on ${new Date(grade.createdAt.toDate()).toLocaleDateString()}` : 
                        'Date not available'
                      }
                    </small>
                  </div>
                ) : (
                  <p className="no-comments">No comments provided</p>
                )}
              </div>
            )}
          </div>

          {/* Performance Analysis */}
          <div className="section">
            <h4>Performance Analysis</h4>
            <div className="performance-chart">
              <div className="performance-item">
                <span className="label">Score:</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${gradeInfo.percentage || 0}%`,
                      backgroundColor: getLetterGradeColor(gradeInfo.letterGrade)
                    }}
                  ></div>
                </div>
                <span className="percentage">{gradeInfo.percentage?.toFixed(1) || 0}%</span>
              </div>
              
              <div className="performance-summary">
                <div className="summary-item">
                  <span className="label">Performance:</span>
                  <span className={`performance-level ${gradeInfo.status || 'pending'}`}>
                    {gradeInfo.status ? gradeInfo.status.charAt(0).toUpperCase() + gradeInfo.status.slice(1) : 'Not Evaluated'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Grade:</span>
                  <span className="grade-letter" style={{ color: getLetterGradeColor(gradeInfo.letterGrade) }}>
                    {gradeInfo.letterGrade || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="print-button" onClick={() => window.print()}>
            🖨️ Print
          </button>
          <button className="close-footer-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default GradeDetailView;
